import React, { useState, useEffect, useRef } from 'react';
import { Box, Typography, Button, TextField, Avatar, Paper, Snackbar, Alert } from '@mui/material';
import { Html5QrcodeScanner } from 'html5-qrcode';
import axiosInstance from '../axiosConfig';

const AccessValidation = () => {
  const [scannedQR, setScannedQR] = useState('');
  const [user, setUser] = useState(null);
  const [error, setError] = useState('');
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState('success');
  const [cameraPermission, setCameraPermission] = useState(null);
  const qrReaderRef = useRef(null);

  const showSnackbar = (message, severity) => {
    setSnackbarMessage(message);
    setSnackbarSeverity(severity);
    setSnackbarOpen(true);
  };

  const handleSnackbarClose = () => {
    setSnackbarOpen(false);
  };


  useEffect(() => {
    // Solo inicializar el escáner si no hay usuario validado
    if (user) return;

    // Esperar a que el DOM esté listo
    const initializeScanner = () => {
      const qrReaderElement = document.getElementById('qr-reader');
      if (!qrReaderElement) {
        console.log('⏳ Esperando elemento qr-reader...');
        setTimeout(initializeScanner, 100);
        return;
      }

      console.log('✅ Elemento qr-reader encontrado, inicializando escáner...');

      const scanner = new Html5QrcodeScanner(
        'qr-reader',
        { 
          fps: 10, 
          qrbox: 250, 
          facingMode: 'environment',
          aspectRatio: 1.0
        },
        false
      );

      const onScanSuccess = async (decodedText) => {
        console.log("📸 Código QR escaneado:", decodedText);
        setScannedQR(decodedText);
        await validateQRCode(decodedText);
        scanner.clear();
      };

      const onScanError = (err) => {
        console.log('🔍 Error de escaneo:', err);
        
        // Manejar diferentes tipos de errores
        if (err.message.includes("Permission denied") || err.message.includes("NotAllowedError")) {
          setCameraPermission('denied');
          setError('Permisos de cámara denegados. Por favor permite el acceso a la cámara.');
        } else if (err.message.includes("No MultiFormat Readers were able to detect the code")) {
          // Este es normal, no mostrar error
          return;
        } else if (err.message.includes("NotReadableError")) {
          setError('Error al acceder a la cámara. Verifica que no esté siendo usada por otra aplicación.');
        } else {
          console.error('🚨 Error escaneando QR:', err);
          setError('Error al escanear el código QR');
        }
      };

      scanner.render(onScanSuccess, onScanError);

      // Guardar referencia del scanner para limpiarlo después
      qrReaderRef.current = scanner;
    };

    // Inicializar después de un pequeño delay para asegurar que el DOM esté listo
    const timer = setTimeout(initializeScanner, 200);

    // Limpiar el escáner cuando el componente se desmonte
    return () => {
      clearTimeout(timer);
      if (qrReaderRef.current) {
        qrReaderRef.current.clear();
        qrReaderRef.current = null;
      }
    };
  }, [user]); // Reiniciar el escáner cuando `user` cambie

  const validateQRCode = async (qrCode) => {
    if (!qrCode) return;

    try {
      console.log("🔗 Enviando QR a la API:", qrCode);
      const response = await axiosInstance.post('/api/validate-qr', { qr_code: qrCode });

      console.log("📩 Respuesta de API:", response.data);

      if (response.data.success) {
        setUser(response.data.user);
        setError('');
      } else {
        setError('Código QR no válido');
        setUser(null);
      }
    } catch (error) {
      console.error('❌ Error en validación:', error);
      setError('Error al validar el código QR');
    }
  };

  const registerEntry = async () => {
    try {
      console.log("✅ Registrando entrada para:", user.id);
      await axiosInstance.post('/api/register-entry', { user_id: user.id, user_type: user.user_type });
      showSnackbar('Entrada registrada con éxito', 'success');
      setUser(null);
    } catch (error) {
      console.error('❌ Error registrando entrada:', error);
      showSnackbar('Error registrando entrada', 'error');
    }
  };

  const registerExit = async () => {
    try {
      console.log("🚪 Registrando salida para:", user.id);
      await axiosInstance.post('/api/register-exit', { user_id: user.id, user_type: user.user_type });
      showSnackbar('Salida registrada con éxito', 'success');
      setUser(null);
    } catch (error) {
      console.error('❌ Error registrando salida:', error);
      showSnackbar('Error registrando salida', 'error');
    }
  };

  // Debug log para verificar que el componente se está renderizando
  console.log('🎯 AccessValidation component rendering...', { user, error, cameraPermission });

  return (
    <Box
      sx={{
        minHeight: '100vh',
        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        p: 2
      }}
    >
      {/* Header */}
      <Box sx={{ textAlign: 'center', mb: 4 }}>
        <Typography
          variant="h3"
          sx={{
            fontWeight: 700,
            color: 'white',
            mb: 1,
            textShadow: '0 2px 4px rgba(0,0,0,0.3)'
          }}
        >
          ACCESS CONTROL
        </Typography>
        <Typography variant="h6" sx={{ color: 'rgba(255,255,255,0.9)' }}>
          Scan QR Code
        </Typography>
      </Box>

      {!user ? (
        <Box sx={{ width: '100%', maxWidth: 400 }}>
          {/* QR Scanner */}
          <Paper 
            elevation={8} 
            sx={{ 
              p: 3, 
              borderRadius: 3,
              background: 'rgba(255, 255, 255, 0.95)',
              backdropFilter: 'blur(20px)',
              mb: 3
            }}
          >
            <div 
              id="qr-reader" 
              ref={qrReaderRef} 
              style={{ 
                width: '100%',
                minHeight: '250px',
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center'
              }}
            >
              {cameraPermission === 'denied' ? (
                <Box sx={{ textAlign: 'center', p: 2 }}>
                  <Typography variant="h6" color="error" sx={{ mb: 2 }}>
                    Camera Access Denied
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    Please allow camera access to scan QR codes
                  </Typography>
                  <Button 
                    variant="contained" 
                    onClick={() => {
                      setCameraPermission(null);
                      setError('');
                      if (qrReaderRef.current) {
                        qrReaderRef.current.clear();
                        qrReaderRef.current = null;
                      }
                    }}
                    sx={{ borderRadius: 2 }}
                  >
                    Try Again
                  </Button>
                </Box>
              ) : (
                <Typography variant="body2" color="text.secondary">
                  Initializing camera...
                </Typography>
              )}
            </div>
          </Paper>

          {/* Manual Entry */}
          <Paper 
            elevation={4} 
            sx={{ 
              p: 2, 
              borderRadius: 2,
              background: 'rgba(255, 255, 255, 0.9)',
              backdropFilter: 'blur(10px)'
            }}
          >
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              Or enter QR code manually:
            </Typography>
            <TextField
              fullWidth
              size="small"
              placeholder="Paste QR code here"
              value={scannedQR}
              onChange={(e) => setScannedQR(e.target.value)}
              onBlur={() => validateQRCode(scannedQR)}
              sx={{ 
                '& .MuiOutlinedInput-root': {
                  borderRadius: 2,
                  background: 'rgba(255, 255, 255, 0.8)',
                },
              }}
            />
            {error && (
              <Alert severity="error" sx={{ mt: 1 }} size="small">
                {error}
              </Alert>
            )}
          </Paper>
        </Box>
      ) : (
        <Box sx={{ width: '100%', maxWidth: 400 }}>
          <Paper 
            elevation={8} 
            sx={{ 
              p: 4, 
              borderRadius: 3,
              background: 'rgba(255, 255, 255, 0.95)',
              backdropFilter: 'blur(20px)',
              textAlign: 'center'
            }}
          >
            {/* User Avatar */}
            <Avatar 
              src={user.photo_url} 
              sx={{ 
                width: 100, 
                height: 100, 
                mx: 'auto', 
                mb: 3,
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                fontSize: '2.5rem',
                border: '4px solid white',
                boxShadow: '0 4px 20px rgba(0,0,0,0.2)'
              }}
            >
              {user.name?.charAt(0)?.toUpperCase()}
            </Avatar>
            
            {/* User Info */}
            <Typography variant="h4" sx={{ fontWeight: 700, mb: 1, color: '#333' }}>
              {user.name}
            </Typography>
            
            <Typography variant="h6" color="text.secondary" sx={{ mb: 2 }}>
              {user.position}
            </Typography>

            {user.user_type === 'visitor' && user.visit_date && (
              <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                Visit Date: {new Date(user.visit_date).toLocaleDateString('en-US')}
              </Typography>
            )}

            {/* Action Buttons */}
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <Button 
                variant="contained" 
                color="success" 
                size="large"
                onClick={registerEntry}
                sx={{
                  py: 2,
                  borderRadius: 2,
                  fontSize: '1.1rem',
                  fontWeight: 600,
                  boxShadow: '0 4px 15px rgba(76, 175, 80, 0.4)'
                }}
              >
                ✅ ALLOW ENTRY
              </Button>
              
              <Button 
                variant="contained" 
                color="error" 
                size="large"
                onClick={registerExit}
                sx={{
                  py: 2,
                  borderRadius: 2,
                  fontSize: '1.1rem',
                  fontWeight: 600,
                  boxShadow: '0 4px 15px rgba(244, 67, 54, 0.4)'
                }}
              >
                ❌ REGISTER EXIT
              </Button>
            </Box>

            {/* Reset Button */}
            <Box sx={{ mt: 3 }}>
              <Button 
                variant="outlined" 
                onClick={() => {
                  setUser(null);
                  setScannedQR('');
                  setError('');
                }}
                sx={{
                  borderRadius: 2,
                  textTransform: 'none',
                  borderColor: '#667eea',
                  color: '#667eea',
                  '&:hover': {
                    borderColor: '#764ba2',
                    backgroundColor: 'rgba(102, 126, 234, 0.1)'
                  }
                }}
              >
                Scan Another QR
              </Button>
            </Box>
          </Paper>
        </Box>
      )}

      {/* Simple Snackbar */}
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={4000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert 
          onClose={handleSnackbarClose} 
          severity={snackbarSeverity}
          sx={{ 
            fontSize: '1rem',
            fontWeight: 600,
            borderRadius: 2
          }}
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default AccessValidation;